import { IconButton, makeStyles, Tooltip, Typography } from '@material-ui/core';
import { Settings } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import authAPI from '../../api/auth';
import { TestId } from '../../components/Testing/TestId';
import { SnackMessage, Text, Toggler } from '../../components/common';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import TenantAccordion from './tenant-accordion';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  title: {
    margin: '0 0 20px',
  },
  accordionText: {
    marginTop: 8,
  },
});

const commitmentOptions = [
  {
    label: 'Enabled',
    value: 'on' as const,
    testId: 'commitment-enabled',
  },
  {
    label: 'Disabled',
    value: 'off' as const,
    testId: 'commitment-disabled',
  },
];

function boolToToggle(v: boolean): 'on' | 'off' {
  switch (v) {
    case true:
      return 'on';
    case false:
      return 'off';
  }
}

function toggleToBool(v: 'on' | 'off'): boolean {
  switch (v) {
    case 'on':
      return true;
    case 'off':
      return false;
  }
}

function TenantConsentsOverviewPage() {
  const classes = useStyles();
  const { rb } = useResourceBundles();
  const { user, updateUser } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const onExpand = useCallback(
    (id: string) => () =>
      setExpandedSection((prev) => (prev === id ? null : id)),
    [],
  );

  const handleChangeFounderCommitment = useCallback(
    async (val: 'on' | 'off') => {
      const value = toggleToBool(val);
      try {
        if (user) {
          const updatedTenant = await authAPI.updateTenant(user?.id, {
            ...user,
            founderCommitmentEnabled: value,
            timeZone: user.timeZone ?? 'US/Eastern',
          });
          updateUser(updatedTenant);
          const message = value
            ? 'Founder Commitments are enabled'
            : 'Founder Commitments are disabled';
          enqueueSnackbar(message, {
            variant: 'success',
          });
        }
      } catch (e: any) {
        const messageError = e.response?.data?.message;
        const message = value
          ? 'An error occurred while activating Founder Commitments. Please, try again.'
          : 'An error occurred while deactivating Founder Commitments. Please, try again.';

        enqueueSnackbar(message, {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, updateUser, user],
  );

  const handleChangeMentorCommitment = useCallback(
    async (val: 'on' | 'off') => {
      const value = toggleToBool(val);
      try {
        if (user) {
          const updatedTenant = await authAPI.updateTenant(user?.id, {
            ...user,
            mentorCommitmentEnabled: value,
            timeZone: user.timeZone ?? 'US/Eastern',
          });
          updateUser(updatedTenant);
          const message = value
            ? `${rb('mentor-u')} Commitments are enabled`
            : `${rb('mentor-u')} Commitments are disabled`;
          enqueueSnackbar(message, {
            variant: 'success',
          });
        }
      } catch (e: any) {
        const messageError = e.response?.data?.message;
        const message = value
          ? `An error occurred while activating ${rb(
              'mentor-u',
            )} Commitments. Please, try again.`
          : `An error occurred while deactivating ${rb(
              'mentor-u',
            )} Commitments. Please, try again.`;

        enqueueSnackbar(message, {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, rb, updateUser, user],
  );

  return (
    <div className={classes.container}>
      <Typography className={classes.title} variant='h4'>
        Commitments and Consents
      </Typography>

      <TenantAccordion
        expanded={expandedSection === 'founder-commitments'}
        onExpand={onExpand('founder-commitments')}
        summary={
          <>
            <Typography variant='h5'>Founder Commitments</Typography>
            <TestId testId={'founder-commitment-enable'}>
              <Toggler<'on' | 'off'>
                value={boolToToggle(user?.founderCommitmentEnabled ?? false)}
                options={commitmentOptions}
                onChange={handleChangeFounderCommitment}
                withIcon={false}
                mini
              />
            </TestId>
          </>
        }
        actions={
          <Tooltip title='Go to Founder Commitments'>
            <IconButton
              component={Link}
              to={Pages.TENANT_CONSENTS_FOUNDERS}
              data-testid='consents-founders-page'>
              <Settings />
            </IconButton>
          </Tooltip>
        }>
        <div className={classes.accordionText}>
          <Text variant='normal'>
            An optional section of the Venture Application where founders
            confirm their agreement to the program's expectations and standards
            as part of applying.
          </Text>
        </div>
      </TenantAccordion>

      <TenantAccordion
        expanded={expandedSection === 'mentor-commitments'}
        onExpand={onExpand('mentor-commitments')}
        summary={
          <>
            <Typography variant='h5'>
              {`${rb('mentor-u')} Commitments`}
            </Typography>
            <TestId testId={'mentor-commitment-enable'}>
              <Toggler<'on' | 'off'>
                value={boolToToggle(user?.mentorCommitmentEnabled ?? false)}
                options={commitmentOptions}
                onChange={handleChangeMentorCommitment}
                withIcon={false}
                mini
              />
            </TestId>
          </>
        }
        actions={
          <Tooltip title={`Go to ${rb('mentor-u')} Commitments`}>
            <IconButton
              component={Link}
              to={Pages.TENANT_CONSENTS_MENTORS}
              data-testid='consents-mentors-page'>
              <Settings />
            </IconButton>
          </Tooltip>
        }>
        <div className={classes.accordionText}>
          <Text variant='normal'>
            {`An optional section of the ${rb(
              'mentor-u',
            )} Application where ${rb(
              'mentors',
            )} confirm their agreement to the program's expectations and standards as part of applying.`}
          </Text>
        </div>
      </TenantAccordion>
    </div>
  );
}

export default TenantConsentsOverviewPage;
